import { DollarSignIcon, PencilIcon } from 'lucide-react'
import { type ReactElement, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import { useOnboarding } from 'components/member_onboarding_v2/OnboardingContext'
import { hasExistingOnThatchEnrollments } from 'components/member_onboarding_v2/utils'
import { FormFormattedNumberFieldDS, FormTelephoneInputDS } from 'components/reusable/form/FormFields'
import { SlabAddressFields } from 'components/reusable/form/SlabAddressFields'
import { Banner, Button, ButtonGroup, useBreakpoint } from 'slab'
import {
  formatPhoneNumber,
  getNumericValueFromNumberOrString,
  validatePOBoxAddressLine1,
  validatePOBoxAddressLine2,
} from 'utils'
import { type MemberOnboardingMember } from 'utils/web_api.schemas'

import { OnboardingClearEnrollmentsModal } from '../OnboardingClearEnrollmentsModal'
import { OnboardingGenericErrorMessage } from '../OnboardingGenericErrorMessage'

interface RenewalsMemberInfoEditFormProps {
  member: MemberOnboardingMember;
  onEditOpen: () => void;
  onSave: () => void;
  onCancel: () => void;
  isMissingHouseholdIncome: boolean;
}

interface MemberInfoEditFormProps {
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  zip: string;
  phone_number: string;
  household_income: string | number | null;
}

export const LABEL_CONTAINER_CLASSES =
  'flex items-center gap-1 body-sm text-gray-500 mb-1 h-5'

const convertToDollars = (cents: number | undefined | null): number => {
  if (cents === undefined || cents === null) return 0
  return cents / 100
}

const RenewalsMemberInfoEditForm: React.FC<RenewalsMemberInfoEditFormProps> = ({
  member,
  onEditOpen,
  onSave,
  onCancel,
  isMissingHouseholdIncome,
}) => {
  const { mapboxAccessToken, onboardingData, updateMemberInfo } =
    useOnboarding()
  const [submitError, setSubmitError] = useState<ReactElement | null>(null)
  const [showAddressEdit, setShowAddressEdit] = useState(false)
  const [showPhoneEdit, setShowPhoneEdit] = useState(false)
  const [showHouseholdIncomeEdit, setShowHouseholdIncomeEdit] = useState(isMissingHouseholdIncome)
  const [pendingFormData, setPendingFormData] =
    useState<MemberInfoEditFormProps | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [isHawaiiState, setIsHawaiiState] = useState(false)
  const { breakpointIsAtLeast } = useBreakpoint()

  const getDefaultValues = () => ({
    address_line1: member?.address_line1 || '',
    address_line2: member?.address_line2 || '',
    city: member?.city || '',
    state: member?.state || '',
    zip: member?.zip || '',
    phone_number: member?.phone_number || '',
    household_income: member?.household_income?.subunits
      ? convertToDollars(member.household_income.subunits)
      : null,
  })

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    reset,
    trigger,
    setError,
    clearErrors,
    watch,
    formState: { errors, isValid, dirtyFields },
  } = useForm<MemberInfoEditFormProps>({
    defaultValues: getDefaultValues(),
    mode: 'onChange',
  })

  // This hook is needed in case the data refreshes.
  useEffect(() => {
    reset(getDefaultValues())
  }, [member])

  // Call onEditOpen when household income is missing on mount
  useEffect(() => {
    if (isMissingHouseholdIncome) {
      onEditOpen()
    }
  }, []) // Only run on mount

  // Check for phone verification restriction
  useEffect(() => {
    if (
      onboardingData?.restrictions?.phone_number_verification ===
      'can_not_start_phone_verification'
    ) {
      setError('phone_number', {
        type: 'manual',
        message:
          'We are unable to verify your phone number. Please double check your number or contact support for more assistance.',
      })
    }
  }, [onboardingData, setError])

  /* Hawaii state validation */
  const stateValue = watch('state')
  useEffect(() => {
    setIsHawaiiState(stateValue === 'HI')
  }, [stateValue, setIsHawaiiState])

  const phoneNumber = member.phone_number
    ? formatPhoneNumber(member.phone_number)
    : ''

  const householdIncome = member.household_income?.subunits !== undefined && member.household_income?.subunits !== null
    ? `$${convertToDollars(member.household_income.subunits).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : 'Not provided'

  const handleAddressEditOpen = () => {
    setShowAddressEdit(true)
    onEditOpen()
  }

  const handleAddressEditClose = () => {
    setShowAddressEdit(false)
    // Reset only address-related fields to default values and clear validation errors
    const defaultValues = getDefaultValues()
    reset({
      address_line1: defaultValues.address_line1,
      address_line2: defaultValues.address_line2,
      city: defaultValues.city,
      state: defaultValues.state,
      zip: defaultValues.zip,
      phone_number: getValues('phone_number'), // Keep current phone value
      household_income: getValues('household_income'),
    })
    onCancel()
  }

  const handlePhoneEditOpen = () => {
    setShowPhoneEdit(true)
    onEditOpen()
  }

  const handlePhoneEditClose = () => {
    setShowPhoneEdit(false)
    // Reset only phone number field to default value and clear validation errors
    const defaultValues = getDefaultValues()
    reset({
      address_line1: getValues('address_line1'), // Keep current address values
      address_line2: getValues('address_line2'),
      city: getValues('city'),
      state: getValues('state'),
      zip: getValues('zip'),
      phone_number: defaultValues.phone_number,
      household_income: getValues('household_income'),
    })
    onCancel()
  }

  const handleHouseholdIncomeEditOpen = () => {
    setShowHouseholdIncomeEdit(true)
    onEditOpen()
  }

  const handleHouseholdIncomeEditClose = () => {
    setShowHouseholdIncomeEdit(false)
    // Reset only household income field to default value and clear validation errors
    const defaultValues = getDefaultValues()
    reset({
      address_line1: getValues('address_line1'), // Keep current values
      address_line2: getValues('address_line2'),
      city: getValues('city'),
      state: getValues('state'),
      zip: getValues('zip'),
      phone_number: getValues('phone_number'),
      household_income: defaultValues.household_income,
    })
    onCancel()
  }

  const handleSubmitMemberInfo = (formData: MemberInfoEditFormProps) => {
    // when the user enters an address, we automatically set is_state_resident to true
    const dataToEdit: any = {}
    if (showAddressEdit) {
      dataToEdit.address_line1 = formData.address_line1
      dataToEdit.address_line2 = formData.address_line2
      dataToEdit.city = formData.city
      dataToEdit.state = formData.state
      dataToEdit.zip = formData.zip
      dataToEdit.is_state_resident = true
    }
    if (showPhoneEdit) {
      dataToEdit.phone_number = formData.phone_number
    }
    if (showHouseholdIncomeEdit) {
      if (formData.household_income) {
        const householdIncomeNumber = getNumericValueFromNumberOrString(formData.household_income)
        dataToEdit.household_income = householdIncomeNumber
      }
    }
    const dataToSubmit = { member: dataToEdit }
    updateMemberInfo.mutate(dataToSubmit, {
      onSuccess: (updatedData) => {
        if (
          updatedData?.restrictions?.phone_number_verification ===
          'can_not_start_phone_verification'
        ) {
          setError('phone_number', {
            type: 'manual',
            message:
              'We are unable to verify your phone number. Please double check your number or contact support for more assistance.',
          })
        } else {
          if (showPhoneEdit) {
            setShowPhoneEdit(false)
            window.segment?.track('[EE] Phone number updated', {
              enrollment_event_id: onboardingData?.enrollment_event.id,
              member_id: onboardingData?.member?.id,
              location: 'member_renewal',
            })
          }
          if (showAddressEdit) {
            setShowAddressEdit(false)
            window.segment?.track('[EE] Address updated', {
              enrollment_event_id: onboardingData?.enrollment_event.id,
              member_id: onboardingData?.member?.id,
              state: formData.state,
              city: formData.city,
              location: 'member_renewal',
            })
          }
          if (showHouseholdIncomeEdit) {
            setShowHouseholdIncomeEdit(false)
            window.segment?.track('[EE] Household income updated', {
              enrollment_event_id: onboardingData?.enrollment_event.id,
              member_id: onboardingData?.member?.id,
              location: 'member_renewal',
            })
          }

          if (submitError) setSubmitError(null)
          onSave()
        }
      },
      onError: (error) => {
        if (error.status === 422) {
          const fieldErrors = error.fieldErrors!
          Object.entries(fieldErrors).forEach(([field, errorMessages]) => {
            setError(field as keyof MemberInfoEditFormProps, {
              type: 'manual',
              message: errorMessages.join(', '),
            })
          })
        } else {
          const errorMessage = <OnboardingGenericErrorMessage error={error} />
          setSubmitError(errorMessage)
        }
      },
    })
  }

  const maybeSubmit = (formData: MemberInfoEditFormProps) => {
    const changedImportantFields =
      dirtyFields.address_line1 ||
      dirtyFields.city ||
      dirtyFields.state ||
      dirtyFields.zip
    const existingEnrollments = hasExistingOnThatchEnrollments(onboardingData)
    if (changedImportantFields && existingEnrollments) {
      // If there are existing enrollments and address related fields are changed,
      // we would need to let the users know that we are going to clear their enrollments
      // because they would need to choose them again due to change in the list of
      // available plans and/or price of those plans.
      // Upons users confirming from the modal, we will submit the form by then.
      setPendingFormData(formData)
      setShowModal(true)
    } else {
      // If not, then we go ahead and submit the form.
      handleSubmitMemberInfo(formData)
    }
  }

  return (
    <>
      <form
        onSubmit={handleSubmit(maybeSubmit)}
        className='bg-white rounded-b-lg border-gray-200'
      >
        <div className='p-4'>
          <div className='border-b border-gray-200 pb-4'>
            {!showPhoneEdit && (
              <div className='flex flex-row justify-between items-center gap-4'>
                <div>
                  <div className={LABEL_CONTAINER_CLASSES}>
                    <span>Phone number</span>
                  </div>
                  <div className='body sensitive'>{phoneNumber}</div>
                </div>
                <Button
                  data-testid='edit-phone-button'
                  leftIcon={PencilIcon}
                  size='small'
                  variant='secondary'
                  onClick={() => handlePhoneEditOpen()}
                >
                  Edit
                </Button>
              </div>
            )}
            {showPhoneEdit && (
              <>
                <div className={LABEL_CONTAINER_CLASSES}>
                  <span>Phone number</span>
                </div>
                <div className='body'>
                  <FormTelephoneInputDS<MemberInfoEditFormProps>
                    error={errors.phone_number}
                    disabled={updateMemberInfo.isPending}
                    fieldId='phone_number'
                    fieldLabel=''
                    register={register}
                    placeholder='(123) 456-7890'
                    validationRule={{ required: 'Phone number is required.' }}
                  />
                </div>
              </>
            )}
          </div>
          <div className='pt-4 border-b border-gray-200 pb-4'>
            {!showAddressEdit && (
              <div className='flex flex-row justify-between items-center gap-4'>
                <div>
                  <div className={LABEL_CONTAINER_CLASSES}>
                    <span>Address</span>
                  </div>
                  <div className='body sensitive'>
                    {member.address_line1}
                    {member.address_line2 && `, ${member.address_line2}`}
                    {member.city && `, ${member.city}`}
                    {member.state && `, ${member.state}`}
                    {member.zip && `, ${member.zip}`}
                  </div>
                </div>
                <Button
                  data-testid='edit-address-button'
                  leftIcon={PencilIcon}
                  size='small'
                  variant='secondary'
                  onClick={() => handleAddressEditOpen()}
                >
                  Edit
                </Button>
              </div>
            )}
            {showAddressEdit && (
              <>
                <div className={LABEL_CONTAINER_CLASSES}>
                  <span>Address</span>
                </div>
                <SlabAddressFields<MemberInfoEditFormProps>
                  accessToken={mapboxAccessToken!}
                  errors={errors}
                  disabled={updateMemberInfo.isPending}
                  getValues={getValues}
                  setValue={setValue}
                  register={register}
                  trigger={trigger}
                  setError={setError}
                  clearErrors={clearErrors}
                  watch={watch}
                  showTopLevelLabel={false}
                  validationRules={{
                    address_line1: {
                      required: 'Street address is required.',
                      validate: (value: string | number | null) =>
                        validatePOBoxAddressLine1(String(value || ''), false),
                    },
                    address_line2: {
                      validate: (value: string | number | null) =>
                        validatePOBoxAddressLine2(String(value || ''), false),
                    },
                    city: {
                      required: 'City is required.',
                    },
                    state: {
                      required: 'State is required.',
                    },
                    zip: {
                      required: 'Zip code is required.',
                    },
                  }}
                />
              </>
            )}
          </div>
          <div className='pt-4'>
            {!showHouseholdIncomeEdit && (
              <div className='flex flex-row justify-between items-center gap-4'>
                <div>
                  <div className={LABEL_CONTAINER_CLASSES}>
                    <span>Annual household income</span>
                  </div>
                  <div className='body sensitive'>{householdIncome}</div>
                </div>
                <Button
                  data-testid='edit-household-income-button'
                  leftIcon={PencilIcon}
                  size='small'
                  variant='secondary'
                  onClick={() => handleHouseholdIncomeEditOpen()}
                >
                  Edit
                </Button>
              </div>
            )}
            {showHouseholdIncomeEdit && (
              <>
                <div className={LABEL_CONTAINER_CLASSES}>
                  <span>Annual household income</span>
                </div>
                <div className='body'>
                  <FormFormattedNumberFieldDS<MemberInfoEditFormProps>
                    error={errors.household_income}
                    description="Checks if you're eligible for government subsidies"
                    disabled={updateMemberInfo.isPending}
                    fieldId='household_income'
                    fieldLabel=''
                    register={register}
                    placeholder='0.00'
                    leadingIcon={DollarSignIcon}
                    validationRule={{
                      required: 'Household income is required.',
                      validate: (value: string | number | null) => {
                        if (!value) return 'Household income is required.'
                        const numericValue = getNumericValueFromNumberOrString(value)
                        if (numericValue >= 100000000) {
                          return 'Household income cannot be $100,000,000 or more'
                        }
                        return true
                      },
                    }}
                  />
                </div>
              </>
            )}
          </div>
        </div>
        {(submitError ||
          onboardingData?.restrictions?.allowance_generation_failed ||
          onboardingData?.restrictions?.member_missing_county) && (
            <div className='px-4'>
              <Banner
                title={
                onboardingData?.restrictions?.allowance_generation_failed
                  ? 'We are missing something'
                  : 'A problem has occurred while processing your request'
              }
                type='critical'
              >
                {submitError}
                {onboardingData?.restrictions?.allowance_generation_failed && (
                  <p>
                    There isn't an allowance set up for your particular scenario.
                    If your address and date of birth look right, please reach out
                    to <a href='mailto:support@thatch.com'>support@thatch.com</a>{' '}
                    for help.
                  </p>
                )}
                {onboardingData?.restrictions?.member_missing_county && (
                  <p>
                    We were unable to determine the correct county from the
                    provided address. If your address looks right, please reach
                    out to{' '}
                    <a href='mailto:support@thatch.com'>support@thatch.com</a> for
                    help.
                  </p>
                )}
              </Banner>
            </div>
        )}
        {isHawaiiState && (
          <div className='px-4'>
            <Banner
              title="Sorry, we can't offer plans in Hawaii"
              type='warning'
            >
              <p>
                We are currently unable to enroll members from Hawaii. Please
                contact our support team at{' '}
                <a href='mailto:support@thatch.com'>support@thatch.com</a> for
                more information.
              </p>
            </Banner>
          </div>
        )}
        {(showAddressEdit || showPhoneEdit || showHouseholdIncomeEdit) && (
          <div className='mt-3 p-4 border-t border-gray-200'>
            <ButtonGroup
              size='large'
              layout={breakpointIsAtLeast('xs') ? 'horizontal' : 'vertical'}
            >
              <Button
                variant='tertiary-deemphasized'
                type='button'
                onClick={() => {
                  if (showAddressEdit) {
                    handleAddressEditClose()
                  }
                  if (showPhoneEdit) {
                    handlePhoneEditClose()
                  }
                  if (showHouseholdIncomeEdit) {
                    handleHouseholdIncomeEditClose()
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                variant='secondary'
                type='submit'
                disabled={
                  updateMemberInfo.isPending || isHawaiiState || !isValid
                }
              >
                Save changes
              </Button>
            </ButtonGroup>
          </div>
        )}
      </form>
      <OnboardingClearEnrollmentsModal
        isModalOpen={showModal}
        onClose={() => {
          setShowModal(false)
          setPendingFormData(null)
        }}
        onSubmit={() => {
          if (pendingFormData) {
            handleSubmitMemberInfo(pendingFormData!)
            setShowModal(false)
          } else {
            // this should never happen
            setError('root', {
              type: 'manual',
              message:
                'Something went wrong submitting this form. Please try again.',
            })
          }
        }}
        description='Your date of birth and address can impact plan cost and availability, so making a change here will remove any plans currently in your cart'
      />
    </>
  )
}

export default RenewalsMemberInfoEditForm
